import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { SubscriptionService } from '@core/subscription/subscription.service';
import { SubscriptionQuery } from '@core/subscription/subscription.query';
import { UsageStatistics, Subscription } from '@core/models/subscription.model';
import { OrganizationQuery } from '@core/organization/organization.query';
import { AppStoreQuery } from '@core/state/app-state.query';
import { UserProfile } from '@core/models/user-profile.model';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, timer, combineLatest } from 'rxjs';
import { catchError, map, switchMap, take, tap, startWith, distinctUntilChanged, shareReplay } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TypedFormGroup } from '@feature/forms/form-model';

@UntilDestroy()
@Component({
  selector: 'wai-tier-limits-indicator',
  templateUrl: './tier-limits-indicator.component.html',
  styleUrls: ['./tier-limits-indicator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TierLimitsIndicatorComponent implements OnInit {
  public asTypedFormGroup = (x: unknown): TypedFormGroup<any> => x as TypedFormGroup<any>;

  @Input() subscription: Subscription | null = null;
  @Input() showProgressBar: boolean = true;
  @Input() limitType: 'ddq' | 'im' = 'ddq';
  @Input() showTierComparison: boolean = false;

  public usageStats$: Observable<UsageStatistics | null>;
  public currentSubscription$: Observable<Subscription | null>;
  public isTrialPeriod$: Observable<boolean>;
  public usagePercentage$: Observable<number>;
  public isUnlimited$: Observable<boolean>;
  public progressBarColor$: Observable<'warn' | 'primary'> = of('primary');
  public tierLimits: Array<{ code: string; name: string; limit: number }> = [];
  public Math = Math;
  public currentTierCode$: Observable<string | null>;

  private isUnlimitedRemaining(remainingDdqRuns: number | null | undefined): boolean {
    return remainingDdqRuns === -1 || remainingDdqRuns === null;
  }

  constructor(
    private subscriptionService: SubscriptionService,
    private subscriptionQuery: SubscriptionQuery,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private organizationQuery: OrganizationQuery,
    private appStoreQuery: AppStoreQuery,
  ) {
    const isStaff$ = this.appStoreQuery.selectUser().pipe(
      map((user) => user?.isStaff === true),
      distinctUntilChanged(),
    );

    this.currentSubscription$ = isStaff$.pipe(
      switchMap((isStaff) => {
        if (isStaff) {
          return of(null);
        }
        // Make sure the subscription has been loaded, then follow the store. The
        // load alone only ever emits one snapshot, so the bar kept the old quota
        // after a plan change (the store was refreshed, this stream was not)
        // until the page was reloaded.
        return this.subscriptionService.getCurrentSubscription().pipe(
          catchError((error) => {
            console.error('Error loading subscription:', error);
            return of(null);
          }),
          take(1),
          switchMap(() => this.subscriptionQuery.selectCurrentSubscription()),
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.isTrialPeriod$ = this.currentSubscription$.pipe(map((sub) => sub?.isTrialPeriod || sub?.isTrial || false));

    const profileUsage$ = this.appStoreQuery.selectUserProfile().pipe(
      map((profile: UserProfile | null) => (profile as any)?.subscription?.usage || null),
      distinctUntilChanged(),
    );

    this.usageStats$ = combineLatest([
      isStaff$,
      this.organizationQuery.selectChosenOrganizationType(),
      this.currentSubscription$,
      profileUsage$,
    ]).pipe(
        switchMap(([isStaff, orgType, subscription, profileUsage]) => {
        if (isStaff) {
          return of(null);
        }

        if (orgType === 'emittent') {
          const q = subscription?.gpOwnFundDdqQuota;
          if (!q) {
            return of(null);
          }
          const stats: UsageStatistics = {
            ddqRunsThisMonth: q.usedThisMonth,
            remainingDdqRuns: q.remaining,
            imGenerationsTotal: 0,
            month: q.month,
            year: q.year,
          };
          return of(stats);
        }

        if (orgType !== 'investor' && orgType !== 'client') {
          return of(null);
        }
        const now = new Date();
        const month = now.getMonth() + 1;
        const year = now.getFullYear();
        const subscriptionUsage = subscription?.usage || profileUsage || null;
        return this.subscriptionQuery.selectUsageStatistics(month, year).pipe(
          map((storeUsage) => storeUsage || subscriptionUsage),
          startWith(subscriptionUsage),
          distinctUntilChanged((prev, curr) => {
            if (prev === curr) {
              return true;
            }
            if (!prev || !curr) {
              return false;
            }
            return (
              prev.ddqRunsThisMonth === curr.ddqRunsThisMonth &&
              prev.remainingDdqRuns === curr.remainingDdqRuns &&
              prev.imGenerationsTotal === curr.imGenerationsTotal &&
              prev.canGenerateIm === curr.canGenerateIm
            );
          }),
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.usagePercentage$ = this.usageStats$.pipe(
      map((stats) => {
        if (!stats || this.isUnlimitedRemaining(stats.remainingDdqRuns)) {
          return 0;
        }

        const total = stats.ddqRunsThisMonth + stats.remainingDdqRuns;
        if (total === 0) {
          return 0;
        }

        return (stats.ddqRunsThisMonth / total) * 100;
      }),
    );

    this.isUnlimited$ = this.usageStats$.pipe(map((stats) => this.isUnlimitedRemaining(stats?.remainingDdqRuns)));

    this.progressBarColor$ = this.usagePercentage$.pipe(
      map((percentage: number) => {
        const value = percentage || 0;
        return value > 80 ? 'warn' : 'primary';
      }),
    );

    this.currentTierCode$ = this.currentSubscription$.pipe(map((sub) => sub?.tier?.code || null));
  }

  ngOnInit(): void {
    // Initialize tier limits for comparison
    this.initializeTierLimits();

    const isStaff$ = this.appStoreQuery.selectUser().pipe(map((user) => user?.isStaff === true));

    combineLatest([isStaff$, this.organizationQuery.selectChosenOrganizationType()])
      .pipe(
        switchMap(([isStaff, orgType]) => {
          if (isStaff || (orgType !== 'investor' && orgType !== 'client' && orgType !== 'emittent')) {
            return of(null);
          }
          return timer(0, 300000).pipe(
            tap(() => {
              this.subscriptionService
                .getCurrentSubscription(true)
                .pipe(catchError(() => of(null)))
                .subscribe(() => {
                  this.cdr.markForCheck();
                });
            }),
          );
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private initializeTierLimits(): void {
    // Define tier limits for comparison (0 = unlimited)
    this.tierLimits = [
      { code: 'lp_free', name: 'Free', limit: 0 },
      { code: 'lp_standard', name: 'Standard', limit: 3 },
      { code: 'lp_professional', name: 'Professional', limit: 10 },
      { code: 'lp_enterprise', name: 'Enterprise', limit: 0 }, // 0 = unlimited
    ];
  }

  public getUsageText(stats: UsageStatistics | null, isTrial: boolean, isUnlimited: boolean): string {
    if (!stats) {
      return '';
    }

    if (isTrial) {
      return `${stats.ddqRunsThisMonth} ${this.translate.instant('INTL_USED_TRIAL_UNLIMITED')}`;
    }

    if (isUnlimited) {
      return `${stats.ddqRunsThisMonth} ${this.translate.instant('INTL_USED_UNLIMITED')}`;
    }

    const total = stats.ddqRunsThisMonth + stats.remainingDdqRuns;
    return `${stats.ddqRunsThisMonth} ${this.translate.instant('INTL_OF')} ${total} ${this.translate.instant(
      'INTL_DDQ_RUNS_USED_THIS_MONTH',
    )}`;
  }

  public getLimitText(stats: UsageStatistics | null, isTrial: boolean, isUnlimited: boolean): string {
    if (isTrial) {
      return this.translate.instant('INTL_UNLIMITED_DURING_TRIAL');
    }

    if (isUnlimited) {
      return this.translate.instant('INTL_UNLIMITED');
    }

    if (!stats) {
      return '';
    }

    return `${stats.remainingDdqRuns} ${this.translate.instant('INTL_REMAINING')}`;
  }

  public getProgressBarColor(percentage: number | null | undefined): 'warn' | 'primary' {
    const value = percentage ?? 0;
    return value > 80 ? 'warn' : 'primary';
  }

  public getTotalLimit(stats: UsageStatistics | null): number {
    if (!stats || this.isUnlimitedRemaining(stats.remainingDdqRuns)) {
      return 0;
    }
    return stats.ddqRunsThisMonth + stats.remainingDdqRuns;
  }

  public getUsagePercentage(stats: UsageStatistics | null): number {
    if (!stats || this.isUnlimitedRemaining(stats.remainingDdqRuns)) {
      return 0;
    }
    const total = stats.ddqRunsThisMonth + stats.remainingDdqRuns;
    if (total === 0) {
      return 0;
    }
    return (stats.ddqRunsThisMonth / total) * 100;
  }

  public getUnlimitedUsagePercentage(stats: UsageStatistics | null): number {
    if (!stats) {
      return 0;
    }
    // For unlimited, show a visual representation based on usage
    // Scale it so it doesn't fill the bar completely
    return Math.min((stats.ddqRunsThisMonth / 50) * 100, 80);
  }

  public getUnlimitedFillWidth(stats: UsageStatistics | null): number {
    return Math.min(this.getUnlimitedUsagePercentage(stats), 100);
  }

  public getTierLimitPercentage(limit: number): number {
    if (limit === 0) {
      return 100; // Unlimited
    }
    // Scale based on max limit (10 for professional)
    const maxLimit = 10;
    return (limit / maxLimit) * 100;
  }
}
